import { supabase } from './supabase'

/** True when fetch failed before an HTTP response (proxy down, offline, CORS, etc.). */
export function isNetworkFetchError(err: unknown): boolean {
  if (typeof navigator !== 'undefined' && !navigator.onLine) return true
  const msg = (err instanceof Error ? err.message : String(err)).toLowerCase()
  return /failed to fetch|network|offline|load failed|networkerror|fetch error|timeout|connection refused|econnrefused|aborted/.test(
    msg
  )
}

export function formatApiNetworkError(path: string): string {
  if (process.env.NODE_ENV === 'development') {
    return (
      `Could not reach the API at ${path}. ` +
      'Start the backend with `npm run proxy-server` (port 3001), or run `npm run serve` for single-process local hosting.'
    )
  }
  return (
    `Could not reach the server (${path}). ` +
    'Check your network connection, or contact support if this persists.'
  )
}

export async function postApiJson(
  path: string,
  body: unknown,
  options?: { auth?: boolean }
): Promise<Record<string, unknown>> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (options?.auth) {
    const { data } = await supabase.auth.getSession()
    const token = data.session?.access_token
    if (token) headers.Authorization = `Bearer ${token}`
  }

  let res: Response
  try {
    res = await fetch(path, { method: 'POST', headers, body: JSON.stringify(body) })
  } catch (err) {
    if (isNetworkFetchError(err)) {
      throw new Error(formatApiNetworkError(path))
    }
    throw err instanceof Error ? err : new Error(String(err))
  }

  const info = (await res.json().catch(() => ({}))) as Record<string, unknown>
  if (!res.ok || info.success === false) {
    throw new Error(String(info.error || `Request failed (HTTP ${res.status})`))
  }
  return info
}
